import React, { useState } from 'react';
import { Modal, DatePicker, Input, Button, Typography, message, Card, Space, Divider } from 'antd';
import { CalendarOutlined, FileTextOutlined, CheckCircleOutlined, UserOutlined, EnvironmentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../../aws/AuthContext';

const { Text, Title } = Typography;
const { TextArea } = Input;

const ModalConfirmarPedido = ({
  isOpen,
  setIsOpen,
  clienteSeleccionado,
  zonaActiva,
  carrito,
  sumaTotalGlobal,
  totalArticulos,
  subtotal,
  impuestos,
  totalDescuento,
  vaciarCarrito,
  config
}) => {
  const { getJwtToken, session } = useAuth();
  const [fechaEntrega, setFechaEntrega] = useState(dayjs().add(1, 'day')); // Por defecto, entrega mañana
  const [notas, setNotas] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGuardarPedido = async () => {
    if (!fechaEntrega) {
      return message.warning("Selecciona una fecha estimada de entrega.");
    }

    if (!clienteSeleccionado || clienteSeleccionado.id === 'DEFAULT') {
      return message.error("Debes seleccionar un cliente válido.");
    }

    setLoading(true);
    try {
      const token = await getJwtToken();
      const payload = {
        cliente_id: clienteSeleccionado.id,
        usuario_id: session?.user?.id || session?.user?.sub || null,
        zona_id: zonaActiva?.id || clienteSeleccionado.zona_id || null,
        fecha_entrega: fechaEntrega.format('YYYY-MM-DD'),
        total: sumaTotalGlobal,
        subtotal: subtotal,
        impuestos: impuestos,
        descuento: totalDescuento,
        notas: notas,
        detalles: carrito.map(item => ({
          producto_id: item.producto_id,
          variante_id: item.variante_id,
          nombre: item.nombre,
          cantidad: item.numItems,
          precio_unitario: item.precioVenta,
          descuento: item.descuento || 0,
          subtotal: item.precioVenta * item.numItems
        }))
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/v1-ordenes-venta`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success("¡Pedido registrado exitosamente!");
      vaciarCarrito();
      setIsOpen(false);
      setNotas('');
    } catch (error) {
      console.error("Error al guardar pedido:", error);
      message.error("Error al guardar el pedido. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={null}
      centered
      width={480}
      title={
        <Space>
          <CheckCircleOutlined style={{ color: '#f59e0b', fontSize: '20px' }} />
          <Title level={4} style={{ margin: 0, fontSize: '18px' }}>Confirmar Toma de Pedido</Title>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
        
        {/* RESUMEN CLIENTE Y ZONA */}
        <Card size="small" style={{ backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}>
          <Space flexDirection="column" align="start" size={4} style={{ width: '100%' }}>
            <Text strong style={{ color: '#d97706' }}>
              <UserOutlined /> Cliente: {clienteSeleccionado?.nombre || 'No seleccionado'}
            </Text>
            {clienteSeleccionado?.identificacion && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                CC/NIT: {clienteSeleccionado.identificacion}
              </Text>
            )}
            <Text type="secondary" style={{ fontSize: '12px', color: '#b45309' }}>
              <EnvironmentOutlined /> Zona: {zonaActiva?.nombre || 'Sin zona asignada'}
            </Text>
          </Space>
        </Card>

        {/* FECHA DE ENTREGA ESTIMADA */}
        <div>
          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>
            <CalendarOutlined /> Fecha de Entrega Estimada:
          </Text>
          <DatePicker
            value={fechaEntrega}
            onChange={(date) => setFechaEntrega(date)}
            disabledDate={(current) => current && current < dayjs().startOf('day')}
            style={{ width: '100%', height: '40px', borderRadius: '8px' }}
            format="DD/MM/YYYY"
            placeholder="Seleccionar fecha"
          />
        </div>

        {/* NOTAS O OBSERVACIONES */}
        <div>
          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>
            <FileTextOutlined /> Observaciones o Notas de Despacho:
          </Text>
          <TextArea
            rows={3}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej: Entregar en jornada de la mañana, llamar antes de salir..."
            style={{ borderRadius: '8px' }}
          />
        </div>

        {/* RESUMEN ECONÓMICO */}
        <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <Text type="secondary">Artículos:</Text>
            <Text strong>{totalArticulos} unidades</Text>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: '16px' }}>TOTAL PEDIDO:</Text>
            <Text strong style={{ fontSize: '20px', color: '#f59e0b' }}>
              ${sumaTotalGlobal.toLocaleString('es-CO')}
            </Text>
          </div>
        </div>

        {/* BOTÓN DE ACCIÓN */}
        <Button
          type="primary"
          size="large"
          loading={loading}
          onClick={handleGuardarPedido}
          style={{
            height: '46px',
            backgroundColor: '#f59e0b',
            borderColor: '#f59e0b',
            fontWeight: '700',
            borderRadius: '10px',
            marginTop: '4px'
          }}
          block
        >
          REGISTRAR PEDIDO
        </Button>
      </div>
    </Modal>
  );
};

export default ModalConfirmarPedido;